import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/utils'

const ringForTone: Record<string, string> = {
  deepBlue: 'peer-focus:ring-curtin-purple/40',
  gray: 'peer-focus:ring-gray-400/40',
  success: 'peer-focus:ring-emerald-500/40',
  destructive: 'peer-focus:ring-rose-500/40',
}

const trackBase =
  'relative inline-block rounded-[24px] bg-gray-300 transition-colors duration-200 ease-in-out ' +
  'peer-focus:outline-none peer-disabled:opacity-50 peer-disabled:cursor-not-allowed ' +
  'after:absolute after:left-[1px] after:top-[1px] after:rounded-full after:bg-white after:shadow-sm ' +
  'after:transition-transform after:duration-200'

const toggleVariants = cva(trackBase, {
  variants: {
    size: {
      sm: 'h-[16px] w-[32px] after:h-[14px] after:w-[14px] peer-checked:after:translate-x-[16px]',
      md: 'h-[20px] w-[40px] after:h-[18px] after:w-[18px] peer-checked:after:translate-x-[20px]', // Figma
      lg: 'h-[24px] w-[48px] after:h-[22px] after:w-[22px] peer-checked:after:translate-x-[24px]',
    },
    tone: {
      deepBlue: 'peer-checked:bg-curtin-purple',
      gray: 'peer-checked:bg-gray-900',
      success: 'peer-checked:bg-emerald-600',
      destructive: 'peer-checked:bg-rose-600',
    },
  },
  defaultVariants: {
    size: 'md',
    tone: 'deepBlue',
  },
})

export interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'color'>, VariantProps<typeof toggleVariants> {
  label?: string
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(({ className, label, tone, size, ...props }, ref) => {
  const ring = ringForTone[tone ?? 'deepBlue']
  return (
    <label className='inline-flex cursor-pointer items-center' aria-label={label}>
      <input ref={ref} type='checkbox' className='peer sr-only' {...props} />
      <span className={cn(toggleVariants({ size, tone }), ring, className)} />
    </label>
  )
})
Toggle.displayName = 'Toggle'
